// KITEIQ deterministic gear matching (frontend).
//
// Mirrors backend/gear_match.py — keep both in sync when changing the formula or
// the status buckets. Given live/forecast wind + the rider's own quiver, decide
// which of their kites/boards fit the conditions. No LLM call: instant & free, so
// it can run for every spot card and every forecast window.
//
// Kite-size model calibrated on the AI Gear Advisor reference points:
//   ~75 kg rider ≈ 12 m² @ 15 kn, 9 m² @ 22 kn, 7 m² @ 28 kn.

export const KITE_C = 190;

const STYLE_FACTOR = {
  freestyle: 0.92, 'big-air': 0.92, bigair: 0.92,
  wave: 0.98, freeride: 1, foil: 1,
};

const MIN_WIND = 8;
const MAX_WIND = 45;
const USABLE_BAND = 1.5;

const BOARD_BY_SPOT = { wave: 'directional', flat: 'twintip', freestyle: 'twintip', chop: 'twintip' };

const roundHalf = (n) => Math.round(n * 2) / 2;

export function recommendKiteSize({ windKnots, weightKg = 75, skill, style }) {
  if (windKnots == null) return null;
  const rideable = Number(windKnots) >= MIN_WIND && Number(windKnots) <= MAX_WIND;
  const w = Math.max(MIN_WIND, Math.min(MAX_WIND, Number(windKnots)));
  let primary = (Number(weightKg || 75) / 75) * (KITE_C / w);
  let f = STYLE_FACTOR[(style || '').toLowerCase()] ?? 1;
  if ((skill || '').toLowerCase() === 'beginner') f *= 1.04;
  primary = Math.max(3, Math.min(18, primary * f));
  return {
    primary: roundHalf(primary),
    min: roundHalf(primary * 0.85),
    max: roundHalf(primary * 1.15),
    rideable,
  };
}

export function parseKiteSize(s) {
  if (s == null) return null;
  const m = String(s).match(/(\d+(?:[.,]\d+)?)/);
  return m ? parseFloat(m[1].replace(',', '.')) : null;
}

export function classifyKite(size, rec, band = USABLE_BAND) {
  if (size == null || !rec) return null;
  const { min, max } = rec;
  if (size >= min && size <= max) return 'ideal';
  if (size < min) return size >= min - band ? 'usable_small' : 'unsuitable_small';
  return size <= max + band ? 'usable_big' : 'unsuitable_big';
}

const STATUS_RANK = {
  ideal: 0, usable_small: 1, usable_big: 1, unsuitable_small: 2, unsuitable_big: 2,
};

export function matchKites(equipment, rec) {
  return (equipment || [])
    .filter((g) => (g.type || '').toLowerCase() === 'kite')
    .map((g) => ({ size: parseKiteSize(g.size), brand: g.brand || '', model: g.model || '' }))
    .filter((g) => g.size != null)
    .map((g) => ({ ...g, status: classifyKite(g.size, rec) }))
    .sort((a, b) => (STATUS_RANK[a.status] - STATUS_RANK[b.status])
      || (Math.abs(a.size - rec.primary) - Math.abs(b.size - rec.primary)));
}

export function kiteVerdict(kites) {
  if (!kites.length) return { level: 'empty', best: null };
  const ideal = kites.find((k) => k.status === 'ideal');
  if (ideal) return { level: 'good', best: ideal };
  const usable = kites.find((k) => k.status === 'usable_small' || k.status === 'usable_big');
  if (usable) return { level: 'warn', best: usable };
  return { level: 'none', best: null };
}

export function detectBoardDiscipline(g) {
  const text = `${g.model || ''} ${g.size || ''} ${g.brand || ''}`.toLowerCase();
  if (/foil|hydrofoil/.test(text)) return 'foil';
  if (/surf|wave|directional|strapless|mutant|skim/.test(text)) return 'directional';
  return 'twintip';
}

export function matchBoards(equipment, spotType) {
  const need = BOARD_BY_SPOT[(spotType || '').toLowerCase()] || 'twintip';
  return (equipment || [])
    .filter((g) => (g.type || '').toLowerCase() === 'board')
    .map((g) => {
      const discipline = detectBoardDiscipline(g);
      return {
        brand: g.brand || '', model: g.model || '', discipline, need,
        status: discipline === need ? 'ideal' : 'usable',
      };
    });
}

// Style + water inferred from a spot's type (matches SpotGearAdvice mapping).
export function styleForSpotType(spotType) {
  return ({ wave: 'wave', flat: 'freeride', freestyle: 'freestyle', chop: 'freeride' })[(spotType || '').toLowerCase()] || 'freeride';
}

export function matchGear(equipment, { windKnots, weightKg, skill, style, spotType }) {
  const rec = recommendKiteSize({ windKnots, weightKg, skill, style: style || styleForSpotType(spotType) });
  if (!rec) return null;
  const kites = matchKites(equipment, rec);
  const boards = matchBoards(equipment, spotType);
  return { rec, kites, boards, verdict: kiteVerdict(kites) };
}

// 5-level "does my own gear work this day" grade for the forecast tiles.
// Returns { level: 1..5, key, rec, best }. Higher level = better fit.
//   5 perfect  — a kite of the rider's lands inside the ideal band
//   4 good     — best kite is just outside the band (<= 0.75 m²)
//   3 marginal — usable but clearly off (<= USABLE_BAND)
//   2 poor     — outside the usable band but rideable wind
//   1 nogo     — wind not rideable (too light/storm) or no kite remotely fits
export function gradeDayFit(equipment, params) {
  const rec = recommendKiteSize(params);
  if (!rec) return { level: 0, key: 'unknown', rec: null, best: null };
  if (!rec.rideable) {
    const tooLight = Number(params.windKnots) < MIN_WIND;
    return { level: 1, key: tooLight ? 'nowind' : 'storm', rec, best: null };
  }
  const kites = matchKites(equipment, rec); // already sorted best-first
  if (!kites.length) return { level: 1, key: 'nogear', rec, best: null };
  const best = kites[0];
  const gap = Math.max(0, rec.min - best.size, best.size - rec.max);
  let level, key;
  if (gap <= 0) { level = 5; key = 'perfect'; }
  else if (gap <= 0.75) { level = 4; key = 'good'; }
  else if (gap <= USABLE_BAND) { level = 3; key = 'marginal'; }
  else if (gap <= USABLE_BAND * 2) { level = 2; key = 'poor'; }
  else { level = 1; key = 'nogo'; }
  return { level, key, rec, best };
}

// True if the rider has at least one kite saved (so we can decide whether to render).
export function hasKites(equipment) {
  return (equipment || []).some((g) => (g.type || '').toLowerCase() === 'kite' && parseKiteSize(g.size) != null);
}
